import useIsMobile from "@/lib/utils/useIsMobile";
import {
    faArrowUpFromBracket,
    faCheckCircle,
    faDownload,
    faX,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "@heroui/react";
import { useState, useEffect } from "react";

interface BeforeInstallPromptEvent extends Event {
    prompt(): Promise<void>;
    readonly userChoice: Promise<{
        outcome: "accepted" | "dismissed";
        platform: string;
    }>;
}

const InstallPWA = () => {
    const { isMobile } = useIsMobile();
    const [installPrompt, setInstallPrompt] =
        useState<BeforeUnloadEvent | null>(null);
    const [isIOS, setIsIOS] = useState(false);
    const [isIOSInstalled, setIsIOSInstalled] = useState(false);
    const [isInstalled, setIsInstalled] = useState(false);
    const [showBanner, setShowBanner] = useState(false);
    const [showInstallInstructions, setShowInstallInstructions] =
        useState(false);

    useEffect(() => {
        if (window.matchMedia("(display-mode: standalone)").matches) {
            setIsInstalled(true);
            return;
        }

        // Detect iOS devices
        const isIOS =
            /iPad|iPhone|iPod/.test(navigator.userAgent) &&
            !("MSStream" in window);
        setIsIOS(isIOS);

        if (isIOS) {
            const iosInstalled = localStorage.getItem("pwa-ios-installed");
            setIsIOSInstalled(!!iosInstalled);

            const iosReminder = localStorage.getItem("pwa-ios-reminder");
            if (iosReminder && parseInt(iosReminder) > Date.now()) {
                return;
            }
        }

        if (!isIOS) {
            const handler = (e: BeforeInstallPromptEvent) => {
                e.preventDefault();
                setInstallPrompt(e);
                setShowBanner(true);
            };

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            window.addEventListener("beforeinstallprompt", handler as any);
            return () =>
                window.removeEventListener(
                    "beforeinstallprompt",
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    handler as any,
                );
        } else {
            // Show iOS banner after 2 seconds if not already installed
            if (!isIOSInstalled) {
                const timer = setTimeout(() => setShowBanner(true), 2000);
                return () => clearTimeout(timer);
            }
        }
    }, [isIOSInstalled]);

    const handleInstallClick = async () => {
        if (isIOS) {
            setShowInstallInstructions(true);
        } else if (installPrompt) {
            try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                await (installPrompt as any).prompt();
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const choiceResult = await (installPrompt as any).userChoice;

                if (choiceResult.outcome === "accepted") {
                    setIsInstalled(true);
                    setShowBanner(false);
                }
            } catch (error) {
                console.error("Install error:", error);
            }

            setInstallPrompt(null);
        }
    };

    const markIOSInstalled = () => {
        localStorage.setItem("pwa-ios-installed", "true");
        setIsIOSInstalled(true);
        setShowInstallInstructions(false);
        setShowBanner(false);
    };

    const closeBanner = () => {
        setShowBanner(false);
        if (isIOS) {
            localStorage.setItem(
                "pwa-ios-reminder",
                (Date.now() + 86400000 * 3).toString(),
            );
        }
    };

    if (isInstalled || !showBanner || !isMobile) return null;

    return (
        <div className="fixed bottom-0 left-0 right-0 z-50">
            {!showInstallInstructions ? (
                <div className="mx-2 mb-2 flex items-center rounded-t-lg border border-gray-200 bg-white p-4 shadow-lg">
                    <div className="flex-1">
                        <h3 className="text-lg font-bold">
                            Download our site!
                        </h3>
                        <p className="text-sm text-gray-600">
                            Install this app on your device for quick access
                        </p>
                    </div>
                    <div className="flex space-x-2">
                        <Button
                            onPress={closeBanner}
                            isIconOnly
                            variant="light"
                        >
                            <FontAwesomeIcon icon={faX} />
                        </Button>
                        <Button onPress={handleInstallClick} color="primary">
                            <FontAwesomeIcon icon={faDownload} />
                            Install
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="mx-2 mb-2 rounded-t-lg border border-gray-200 bg-white p-4 shadow-lg">
                    <div className="mb-3 flex items-center justify-between">
                        <h3 className="text-lg font-bold">How to Install</h3>
                        <Button
                            onPress={() => setShowInstallInstructions(false)}
                            variant="light"
                        >
                            <FontAwesomeIcon icon={faX} />
                        </Button>
                    </div>

                    <div className="mb-4">
                        <p className="mb-2">
                            1. Tap the{" "}
                            <span className="rounded bg-gray-100 px-2 py-1">
                                Share
                                <FontAwesomeIcon
                                    icon={faArrowUpFromBracket}
                                    className="ml-2"
                                />
                            </span>{" "}
                            icon in your browser.
                        </p>
                        <p className="mb-2">
                            2. Scroll down and select{" "}
                            <span className="rounded bg-gray-100 px-2 py-1">
                                Add to Home Screen
                            </span>
                            .
                        </p>
                        <p className="mb-2">
                            3. Tap{" "}
                            <span className="rounded bg-gray-100 px-2 py-1">
                                Add
                            </span>{" "}
                            to confirm.
                        </p>
                    </div>

                    <div className="flex justify-between">
                        <Button
                            onPress={() => setShowInstallInstructions(false)}
                            variant="flat"
                        >
                            Cancel
                        </Button>
                        <Button onPress={markIOSInstalled} color="primary">
                            <FontAwesomeIcon icon={faCheckCircle} />
                            <span>I've Installed It</span>
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InstallPWA;
